/**
 * src/shell/portal/components/shop/GarageProductCard.tsx
 *
 * Sovereign Garage Product Card:
 * - Certified Sprint 11 Theme Token Compliance (zero raw hex codes, uses var(--theme-*))
 * - Relative import to ../media/UniversalMediaCard.js & ../media/VideoEmbedPlayer.js
 * - Strict Event Propagation Isolation: "Play Demo" and "View Datasheet" calls e.stopPropagation()
 * - Z-Index Hierarchy: Embedded demo modal mounts at z-9999, safely exceeding sticky cart drawer (z-50)
 * - Escrow assurance badges, multi-currency display, and clean backdrop teardown
 */

import React, { useState } from 'react';
import { ShieldCheck, Play, FileText, ShoppingCart, Star, ExternalLink, X } from 'lucide-react';
import type { GarageListing } from '../../../../plugins/commerce/garage-shop-contracts.js';
import type { MediaFeedItem } from '../../../../domain/media/media-feed-contracts.js';
import { UniversalMediaCard } from '../media/UniversalMediaCard.js';
import { VideoEmbedPlayer } from '../media/VideoEmbedPlayer.js';

export interface GarageProductCardProps {
  product: GarageListing;
  onSelectProduct?: (product: GarageListing) => void;
  onBuyViaEscrow?: (product: GarageListing) => void;
}

export const GarageProductCard: React.FC<GarageProductCardProps> = ({
  product,
  onSelectProduct,
  onBuyViaEscrow,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [imgError, setImgError] = useState(false);

  // Construct synthetic MediaFeedItem if demo video or spec PDF is present
  const hasDemoVideo = Boolean(product.demoVideoUrl || product.mediaPayload?.payload?.video);
  const hasSpecPdf = Boolean(product.specPdfUrl || product.mediaPayload?.payload?.document);

  const demoMediaItem: MediaFeedItem | undefined = product.mediaPayload || (product.demoVideoUrl ? {
    id: `media_prod_${product.listingId}`,
    creatorDid: product.creatorDid,
    creatorHandle: product.sellerDisplayName || '@creator',
    createdAt: product.createdAt,
    type: 'video_embed',
    sessionState: 'prerecorded',
    title: `${product.title} - Product Demo`,
    description: product.description,
    payload: {
      video: {
        provider: product.demoVideoUrl.includes('vimeo') ? 'vimeo' : 'youtube',
        platformId: 'demo',
        embedUrl: product.demoVideoUrl,
        isLiveStream: false,
      },
    },
    stats: {
      views: 120,
      likes: 18,
      commentCount: 4,
    },
    context: {
      channelId: product.channelId,
      shopProductId: product.listingId,
    },
  } : undefined);

  const handleCardClick = () => {
    onSelectProduct?.(product);
  };

  const handlePlayDemoClick = (e: React.MouseEvent) => {
    // Touchpoint 2: Explicitly stop bubbling so parent card click / buy handler does NOT fire
    e.stopPropagation();
    setIsDemoModalOpen(true);
  };

  const handlePdfClick = (e: React.MouseEvent) => {
    // Touchpoint 2: Explicitly stop bubbling
    e.stopPropagation();
    const pdfUrl = product.specPdfUrl || product.mediaPayload?.payload?.document?.fileUrl;
    if (pdfUrl && typeof window !== 'undefined') {
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleBuyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onBuyViaEscrow) {
      onBuyViaEscrow(product);
    } else {
      onSelectProduct?.(product);
    }
  };

  return (
    <>
      <div
        onClick={handleCardClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          width: '260px',
          backgroundColor: 'var(--theme-bg-card, #11141d)',
          borderRadius: '16px',
          overflow: 'hidden',
          border: isHovered
            ? '1px solid var(--theme-accent, #6366f1)'
            : '1px solid var(--theme-border, rgba(255,255,255,0.08))',
          boxShadow: isHovered
            ? '0 12px 28px rgba(0, 0, 0, 0.45)'
            : '0 4px 12px rgba(0, 0, 0, 0.25)',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        {/* Product Media / Thumbnail Preview */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 10',
            backgroundColor: 'var(--theme-bg-input, #161b26)',
            overflow: 'hidden',
          }}
        >
          {product.imageUrl && !imgError ? (
            <img
              src={product.imageUrl}
              alt={product.title}
              onError={() => setImgError(true)}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: isHovered ? 'scale(1.04)' : 'scale(1)',
                transition: 'transform 0.3s ease',
              }}
            />
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '36px',
                color: 'var(--theme-text-muted, #94a3b8)',
              }}
            >
              🛍️
            </div>
          )}

          {/* Fulfillment Tag */}
          <div
            style={{
              position: 'absolute',
              top: '8px',
              left: '8px',
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              backdropFilter: 'blur(4px)',
              color: 'var(--theme-accent, #6366f1)',
              fontSize: '9px',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              padding: '3px 8px',
              borderRadius: '6px',
              border: '1px solid var(--theme-border, rgba(255,255,255,0.08))',
            }}
          >
            {product.fulfillmentType.replace('_', ' ')}
          </div>

          {/* Embedded Media Action Badges (Isolated Bubbling) */}
          <div
            style={{
              position: 'absolute',
              bottom: '8px',
              right: '8px',
              display: 'flex',
              gap: '6px',
              zIndex: 10,
            }}
          >
            {hasDemoVideo && (
              <button
                type="button"
                onClick={handlePlayDemoClick}
                title="Play interactive demo"
                style={{
                  padding: '4px 10px',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(0, 0, 0, 0.85)',
                  border: '1px solid var(--theme-accent, #6366f1)',
                  color: 'var(--theme-text-primary, #ffffff)',
                  fontSize: '10px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
                }}
              >
                <Play size={10} fill="currentColor" color="var(--theme-accent, #6366f1)" />
                <span>Play Demo</span>
              </button>
            )}

            {hasSpecPdf && (
              <button
                type="button"
                onClick={handlePdfClick}
                title="View PDF Datasheet"
                style={{
                  padding: '4px 8px',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(0, 0, 0, 0.85)',
                  border: '1px solid var(--theme-border, rgba(255,255,255,0.12))',
                  color: 'var(--theme-text-muted, #94a3b8)',
                  fontSize: '10px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <FileText size={10} />
                <span>PDF</span>
              </button>
            )}
          </div>
        </div>

        {/* Product Details Body */}
        <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
            <h4
              style={{
                margin: 0,
                fontSize: '13px',
                fontWeight: 700,
                color: 'var(--theme-text-primary, #ffffff)',
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {product.title}
            </h4>
          </div>

          <p
            style={{
              margin: 0,
              fontSize: '11px',
              color: 'var(--theme-text-muted, #94a3b8)',
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {product.description}
          </p>

          {/* Pricing & Escrow Pill */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: '6px',
              borderTop: '1px solid var(--theme-border, rgba(255,255,255,0.08))',
            }}
          >
            <div>
              <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--theme-text-primary, #ffffff)' }}>
                ${product.priceUsd.toFixed(2)}
              </div>
              <div
                style={{
                  fontSize: '10px',
                  color: 'var(--theme-accent, #6366f1)',
                  fontFamily: 'var(--theme-font-mono, monospace)',
                }}
              >
                {product.priceTokens} TOKENS
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '10px',
                fontWeight: 600,
                color: 'var(--theme-accent, #6366f1)',
                backgroundColor: 'rgba(99, 102, 241, 0.12)',
                padding: '3px 8px',
                borderRadius: '6px',
                border: '1px solid rgba(99, 102, 241, 0.25)',
              }}
            >
              <ShieldCheck size={12} />
              <span>Escrow</span>
            </div>
          </div>

          {/* Action Row */}
          <button
            type="button"
            onClick={handleBuyClick}
            style={{
              marginTop: '4px',
              width: '100%',
              padding: '8px 12px',
              borderRadius: '8px',
              backgroundColor: 'var(--theme-accent, #6366f1)',
              color: 'var(--theme-text-primary, #ffffff)',
              border: 'none',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'opacity 0.15s ease',
            }}
          >
            <ShoppingCart size={13} />
            <span>Buy via Escrow</span>
          </button>
        </div>
      </div>

      {/* High-Priority Product Demo Modal (z-9999, exceeds sticky cart drawer z-50) */}
      {isDemoModalOpen && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            setIsDemoModalOpen(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999, // Guarantees isolation over sticky cart drawer (z-50)
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '680px',
              backgroundColor: 'var(--theme-bg-card, #11141d)',
              borderRadius: '16px',
              border: '1px solid var(--theme-border, rgba(255,255,255,0.08))',
              overflow: 'hidden',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.7)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '14px 18px',
                borderBottom: '1px solid var(--theme-border, rgba(255,255,255,0.08))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Play size={16} color="var(--theme-accent, #6366f1)" />
                <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: 'var(--theme-text-primary, #ffffff)' }}>
                  {product.title} &mdash; Interactive Demo
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsDemoModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--theme-text-muted, #94a3b8)',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Player Body */}
            <div style={{ padding: '16px' }}>
              {demoMediaItem ? (
                <UniversalMediaCard item={demoMediaItem} />
              ) : (
                <div
                  style={{
                    padding: '30px',
                    textAlign: 'center',
                    color: 'var(--theme-text-muted, #94a3b8)',
                  }}
                >
                  No preview video attached to this product listing.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
